/**
 * Cockpit web entrypoint.
 *
 * Order of operations:
 *   1. Import global styles FIRST so Tailwind base + tokens.css load before
 *      any component's scoped/inline styles.
 *   2. Import side-effect state.ts so localStorage hydration happens before
 *      the first component reads it.
 *   3. Mount Vue app with router.
 */

import './styles/global.css';
import './state';
// Brand-udtrykket ([D-471]): sætter/fjerner klassen brand-ui på <html>.
import './composables/useBrandUi';

import { createApp } from 'vue';

import App from './App.vue';
import { auth, ensureSessionLoaded } from './composables/useAuth';
import { vDuration, vFullDate, vTip } from './directives/tip';
import { installSavingFetchInterceptor } from './lib/saving';
import { installPrintMode } from './lib/print-mode';
import { router } from './router';

// Global 401-interceptor: hvis et /api/-kald (ikke auth-flowet selv) svarer 401,
// er sessionen udløbet → ryd bruger og send til login-siden.
const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const res = await originalFetch(input, init);
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (res.status === 401 && url.includes('/api/') && !url.includes('/api/auth/')) {
    auth.user = null;
    // [D-420]: kampagne-dashboardets monitor-visning har sit eget login (90
    // dage) og viser selv Log ind-knappen. Den må ikke rives over på login-
    // siden, fordi appens session er udløbet: så ville skærmen stå på SSO.
    // Adressen tjekkes på hashen, fordi kaldet kan komme før routeren har
    // bekræftet ruten (session-hydreringen ved boot).
    if (/^#\/marketing\/campaigns\/dashboards\/[0-9a-f]{32}/i.test(window.location.hash)) return res;
    if (!window.location.hash.startsWith('#/login')) {
      window.location.hash = '#/login';
    }
  }
  return res;
};

// Global "Gemmer data …"-indikator: wrap window.fetch (oven på 401-interceptoren
// ovenfor), så enhver muterende API-kald på tværs af alle sider driver footer-
// slide-up'en (SavingIndicator.vue) automatisk (session 49).
installSavingFetchInterceptor();

// Print-tilstand (§27): browserens egen udskrift (Ctrl+P) skal give de samme
// KONKRETE datoer som appens PDF-knapper — "I dag" i et gemt dokument er
// ubrugelig for den der åbner det senere.
installPrintMode();

// Kick session-hydrering i gang UDEN at blokere first paint (F5): router-guarden
// afventer ensureSessionLoaded() før den slipper en beskyttet rute igennem, så
// app-shellen (sidebar/filter) males med det samme mens de to auth-kald kører.
void ensureSessionLoaded();

const app = createApp(App);
app.use(router);
app.directive('tip', vTip); // stylet tooltip-direktiv ([D-088], 03-ui-design §12.5)
app.directive('fulldate', vFullDate); // fuld dato bag relative datoer (12-conventions §94)
app.directive('duration', vDuration); // fuld varighed bag kort varighed (12-conventions §100)
app.mount('#app');
